import type { ReactFCWithChildren } from '~/types';
import {
  FacebookFilled,
  InstagramFilled,
  YoutubeFilled,
} from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import { useStore } from '@tanstack/react-store';
import React from 'react';
import { data } from '~/data/basic';
import menuData from '~/data/menu';
import { langStore } from '~/store/langStore';
import { cn } from '~/utils';
import RemoteImage from './RemoteImage';
import { WemsicalIcon } from './wemsical-logo';

const MenuLinks: ReactFCWithChildren = ({ children }) => (
  <div className="mx-2 flex h-30 flex-col flex-wrap justify-center gap-x-6 gap-y-2 px-2 text-sm font-bold text-beige3 sm:text-base md:justify-start">
    {children}
  </div>
);

const iconBaseStyle = 'text-[40px] text-beige3';
const facebookHover = 'hover:text-blue-500';
const instagramHover = 'hover:text-yellow-500';
const youtubeHover = 'hover:text-red-500';

export function SocialMedia() {
  return (
    <div className="mb-10 flex justify-center gap-4 md:justify-around">
      <a
        href="https://www.facebook.com/malomfesztival"
        target="_blank"
        rel="noreferrer noopener"
        data-umami-event="facebook"
        className={cn(iconBaseStyle, facebookHover)}
      >
        <FacebookFilled />
      </a>
      <a
        href="https://www.instagram.com/malomfesztival/"
        target="_blank"
        rel="noreferrer noopener"
        data-umami-event="instagram"
        className={cn(iconBaseStyle, instagramHover)}
      >
        <InstagramFilled />
      </a>
      <a
        href="https://www.youtube.com/channel/UCcPwvZv02sThpQpQNEynEKg"
        target="_blank"
        rel="noreferrer noopener"
        data-umami-event="youtube"
        className={cn(iconBaseStyle, youtubeHover)}
      >
        <YoutubeFilled />
      </a>
    </div>
  );
}

export default function Footer() {
  const locale = useStore(langStore, s => s);
  const allMenus = Object.values(menuData.menus).filter(menu => menu[locale]);

  // Filter to only show programmes, tickets, and info
  const allowedMenuKeys = ['programmes', 'tickets', 'info'];
  const menus = allMenus.filter((menu) => {
    const menuKey = Object.keys(menuData.menus).find(key => menuData.menus[key] === menu);
    return menuKey && allowedMenuKeys.includes(menuKey);
  });

  return (
    <div className="relative">
      <div
        className={cn(`
      clip-path-footer
      text-rgb(255,249,241)
      absolute -top-55 left-0
      w-full
      bg-orange md:-top-30
      md:px-24 md:py-24
      md:pb-24
      `)}
      >
        <div className="
      flex
      h-full
      flex-col
      justify-center
      pt-48
      md:grid
      md:grid-cols-[1fr_auto_1fr]
      md:items-end
      md:justify-between md:pt-22"
        >
          <div className="h-12 md:hidden" />
          <MenuLinks>
            {Object.values(menus).map((menu, index) => (
              <React.Fragment key={index}>
                {menu.subitems
                  ? (
                      Object.values(menu.subitems).map(subitem => (
                        <Link to={subitem.to} key={`link${subitem.to}`} className="hover:text-red">
                          {subitem[locale]}
                        </Link>
                      ))
                    )
                  : (
                      <Link to={menu.to} className="hover:text-red">
                        {menu[locale]}
                      </Link>
                    )}
              </React.Fragment>
            ))}
          </MenuLinks>
          <div className="m-4 text-center">
            <SocialMedia />
            <div className="text-beige3">{data.contactEmail}</div>
          </div>
          <div className="flex justify-center md:justify-end md:justify-self-end">
            <div className="flex flex-col items-end gap-5 md:flex-row md:items-center">
              <div className="flex flex-col items-center gap-5 md:mr-5 md:gap-5">
                <WemsicalIcon className="w-1/4 text-white md:w1/2" />
                <img src="/malom-feher.svg" alt="Malom Logo" />
              </div>
              <RemoteImage src="/logo25.webp" className="size-36" alt="Logo" />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
